// @ts-strict-ignore
import { ConfigFileSection } from '@/types';
import { ConfigFileManagedButton } from '@/components/HOCs/ConfigFileManagedMessage';
import { FormInstance, useForm } from 'antd/lib/form/Form';
import Modal from 'antd/lib/modal/Modal';
import Spin from 'antd/lib/spin';
import { ErrorFallback } from './ErrorBoundary';
import { flatMap } from 'lodash';
import * as React from 'react';
import { IntegrationInstallationId, IntegrationProviderType, IntegrationType } from '../types';
import { integrationModalContentUsingFormInstance } from '../helpers/formHelpers';
import { RootState } from '../reducers/reducers';
import { selectOrgProviders } from '../selectors/selectors';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AppRouteComponentMatchParams } from '../typescriptHelpers';
import { oauthInstallationUrlForIntegrationType } from '../helpers/integrationHelpers';
import Link from 'antd/lib/typography/Link';
import Text from 'antd/lib/typography/Text';
import { formFieldsFromSettingFields } from '../helpers/settingFieldHelpers';
import { UpdateIntegrationModalData } from './setup/useUpdateIntegrationModal';
import { globalMessage } from '../utils/globalMessage';
import { useIntegrationSettingFields } from '../utils/hooks/useIntegrationSettingFields';

interface IntegrationConfigurationProps extends UpdateIntegrationModalData {
    onClose: () => void;
}

const IntegrationConfiguration: React.FC<IntegrationConfigurationProps> = (props: IntegrationConfigurationProps) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const [formIsValid, setFormIsValid] = React.useState(false);
    const { settingFields, isLoadingSettingFields, isSettingFieldsError } = useIntegrationSettingFields(
        props.orgId,
        props.appId,
        props.integration.installationId,
    );

    const integrationProvider = useSelector((state: RootState) => {
        const integrationProviders = selectOrgProviders(state, props.orgId);
        const provider = integrationProviders.find((provider) => provider.id === props.integration?.providerId);

        return provider ?? null;
    });

    const onFieldsChange = (_: IntegrationType, form: FormInstance) => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));

        const isValid = errors.length === 0;
        setFormIsValid(isValid);
    };

    const onSubmit = (
        integrationInstallationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        form: FormInstance,
    ) => {
        form.validateFields()
            .then((values) => {
                // BE-defined fields already use the save payload's shape
                if (!settingFields) {
                    throw Error('Invalid form values');
                }
                props.updateIntegration(integrationInstallationId, integrationType, values);
            })
            .catch((e) => {
                globalMessage.error(e.message);
                setFormIsValid(false);
            });
    };

    const onClose = () => {
        form.resetFields();

        props.onClose();
    };

    const [form] = useForm();

    const fields = settingFields ? formFieldsFromSettingFields(settingFields, props.integration.id) : [];
    const modalContent = integrationModalContentUsingFormInstance(
        form,
        'vertical',
        props.integration.id,
        props.integration.installationId,
        fields,
        onFieldsChange,
        onSubmit,
    );
    const additionalInfoForProvider =
        integrationProvider ?
            additionalInfoNodeForProvider(
                integrationProvider.type,
                props.integration.id,
                props.orgId,
                orgIdParam,
                appIdParam,
            )
        :   null;

    return (
        <Modal
            title={`Complete ${props.integration.name} setup`}
            open={props.open}
            confirmLoading={props.isLoading}
            maskClosable={props.closable}
            closable={props.closable}
            onCancel={onClose}
            footer={
                isSettingFieldsError ? null : (
                    <ConfigFileManagedButton
                        configFileSection={ConfigFileSection.Integrations}
                        onClick={() => form.submit()}
                        disabled={!formIsValid}
                        loading={props.isLoading}
                        type="primary"
                    >
                        Submit
                    </ConfigFileManagedButton>
                )
            }
            centered
            destroyOnClose
            width={props.integration.id === IntegrationType.CircleCI ? 700 : 520}
        >
            {isSettingFieldsError ?
                <ErrorFallback />
            :   <div>
                    {isLoadingSettingFields ?
                        <Spin />
                    :   modalContent}
                    {additionalInfoForProvider && <div style={{ marginTop: 24 }}>{additionalInfoForProvider}</div>}
                </div>
            }
        </Modal>
    );
};

export default IntegrationConfiguration;

export const additionalInfoNodeForProvider = (
    providerType: IntegrationProviderType,
    integrationType: IntegrationType,
    orgId: string,
    orgIdParam: string,
    appIdParam: string,
    integrationInstallationId?: IntegrationInstallationId,
) => {
    if (providerType === IntegrationProviderType.GitHub) {
        const installURL = oauthInstallationUrlForIntegrationType(
            integrationType,
            orgId,
            orgIdParam,
            appIdParam,
            undefined,
            integrationInstallationId,
        );
        return (
            <Text>
                Don&apos;t see your repo? <Link href={installURL}>Authorize more repos.</Link>
            </Text>
        );
    }

    return null;
};
